import { Menu, MenuProps } from 'antd';
import classnames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/Button';

import AuthContext, { AuthContextInterface } from '../../../contexts/Auth/AuthContext';
import { NavigationItem } from '../../../helpers/generateNavigationItemsKeys';

import './VerticalNavTransform.scss';

export interface VerticalNavTransformProps {
  withOverlayMenu?: boolean;
  navigationData: NavigationItem[];
}

export const createMenuItems = (
  navigationData: NavigationItem[],
  navigate: NavigateFunction,
  auth: AuthContextInterface,
): MenuProps['items'] =>
  navigationData
    .filter((item) => !item.isShown || item.isShown(auth))
    .map((item) => ({
      key: item.key,
      label: item.label,
      children: item.children ? createMenuItems(item.children, navigate, auth) : null,
      onClick: item.slug ? (): void => void navigate(item.slug as string) : (): null => null,
    }));

// TODO: refactor?
const findSelectedKey = (
  navigationData: NavigationItem[],
  pathName: string,
  parentKeys: string[] = [],
  initialValue: [string[], string[]] = [[], []],
): [string[], string[]] =>
  navigationData.reduce((result, navigationItem) => {
    if (navigationItem.children) {
      return findSelectedKey(navigationItem.children, pathName, [...parentKeys, navigationItem.key], result);
    }
    return pathName.includes(navigationItem.slug as string) ? [[navigationItem.key], parentKeys] : result;
  }, initialValue);

export const VerticalNavTransform = React.memo<VerticalNavTransformProps>(
  ({ withOverlayMenu = false, navigationData }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const menuButtonToggler = useRef<HTMLDivElement>(null);
    const auth = useContext(AuthContext);

    const menuItems = createMenuItems(navigationData, navigate, auth);
    const [selectedKey, openedDropdowns] = findSelectedKey(navigationData, pathname);

    const [isMenuActive, setIsMenuActive] = useState<boolean>(false);

    const handleClickMenu = useCallback(
      (value = !isMenuActive) => {
        setIsMenuActive(value);
      },
      [isMenuActive],
    );

    useEffect(() => {
      function handleClickOutside(event: Event): void {
        if (
          menuButtonToggler.current &&
          event.target instanceof Node &&
          !menuButtonToggler.current.contains(event.target)
        ) {
          handleClickMenu(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);

      return (): void => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [menuButtonToggler, handleClickMenu]);

    return (
      <div>
        <div className="nav-container" ref={menuButtonToggler}>
          {withOverlayMenu && (
            <Button
              className={classnames('nav-button', { 'nav-button__active': isMenuActive })}
              onClick={(): void => handleClickMenu()}
            >
              Меню
              <div
                className={classnames('nav-button-arrow', {
                  'nav-button-arrow__transform': isMenuActive,
                })}
              />
            </Button>
          )}

          <Menu
            defaultOpenKeys={openedDropdowns}
            selectedKeys={selectedKey}
            mode="inline"
            items={menuItems}
            className={classnames('nav', {
              'nav-with-overlay': withOverlayMenu,
              nav__overlay: !withOverlayMenu || isMenuActive,
            })}
            onClick={(): void => handleClickMenu()}
          />
        </div>
        {isMenuActive && withOverlayMenu && <div className="overlay" />}
      </div>
    );
  },
);
